import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
    useComputeSoilHumidityForMissingImages,
    useRecomputeSoilHumidityForAllImages,
    useSampleComputeStatus,
    useComputeRootDetectionForMissingImages,
    useRecomputeRootDetectionForAllImages,
    useComputeRootInferenceForMissingImages,
    useRecomputeRootInferenceForAllImages,
} from '@/services/image.service';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDownIcon } from 'lucide-react';
import BackofficePage, {
    DataCard,
    DataTable,
    TH,
    TR,
    TD,
} from '../../components/BackofficePage';

export default function BackOfficeCompute() {
    const { data: samples, isLoading, error } = useSampleComputeStatus();
    const { mutate: computeSoilHumidityForMissingImages } =
        useComputeSoilHumidityForMissingImages();
    const { mutate: recomputeSoilHumidityForAllImages } =
        useRecomputeSoilHumidityForAllImages();
    const { mutate: computeRootDetectionForMissingImages } =
        useComputeRootDetectionForMissingImages();
    const { mutate: recomputeRootDetectionForAllImages } =
        useRecomputeRootDetectionForAllImages();
    const { mutate: computeRootInferenceForMissingImages } =
        useComputeRootInferenceForMissingImages();
    const { mutate: recomputeRootInferenceForAllImages } =
        useRecomputeRootInferenceForAllImages();

    if (isLoading)
        return (
            <BackofficePage title="Compute" description="Loading…">
                <Skeleton className="w-full h-[400px] bg-[#1e242a]" />
            </BackofficePage>
        );
    if (error)
        return (
            <BackofficePage title="Compute" description="Error">
                <div className="text-red-400 text-sm">{error.toString()}</div>
            </BackofficePage>
        );

    return (
        <BackofficePage
            title="Compute"
            description="Run or re-run analysis pipelines per sample."
        >
            <DataCard>
                <DataTable>
                    <thead className="bg-[#0f141a]">
                        <tr>
                            <TH>Project</TH>
                            <TH>Sample</TH>
                            <TH align="center">Sample ID</TH>
                            <TH align="center">Soil humidity</TH>
                            <TH align="center">Root detection</TH>
                            <TH align="center">Root inference</TH>
                            <TH align="center">Root geometry</TH>
                        </tr>
                    </thead>
                    <tbody>
                        {samples?.map((sample, index) => (
                            <TR key={index}>
                                <TD>{sample.projectName}</TD>
                                <TD>{sample.sampleName}</TD>
                                <TD align="center" mono>
                                    {sample.sampleId}
                                </TD>
                                <TD align="center">
                                    <ComputeMenu
                                        missingLabel={`Compute missing (${sample.missingImages})`}
                                        recomputeLabel={`Recompute all (${sample.totalImages})`}
                                        onMissing={() =>
                                            computeSoilHumidityForMissingImages(
                                                {
                                                    sampleId: sample.sampleId,
                                                },
                                            )
                                        }
                                        onRecompute={() =>
                                            recomputeSoilHumidityForAllImages({
                                                sampleId: sample.sampleId,
                                            })
                                        }
                                    />
                                </TD>
                                <TD align="center">
                                    <ComputeMenu
                                        missingLabel="Compute missing"
                                        recomputeLabel={`Recompute all (${sample.totalImages})`}
                                        onMissing={() =>
                                            computeRootDetectionForMissingImages(
                                                {
                                                    sampleId: sample.sampleId,
                                                },
                                            )
                                        }
                                        onRecompute={() =>
                                            recomputeRootDetectionForAllImages(
                                                {
                                                    sampleId: sample.sampleId,
                                                },
                                            )
                                        }
                                    />
                                </TD>
                                <TD align="center">
                                    <ComputeMenu
                                        missingLabel="Compute missing"
                                        recomputeLabel={`Recompute all (${sample.totalImages})`}
                                        onMissing={() =>
                                            computeRootInferenceForMissingImages(
                                                {
                                                    sampleId: sample.sampleId,
                                                },
                                            )
                                        }
                                        onRecompute={() =>
                                            recomputeRootInferenceForAllImages(
                                                {
                                                    sampleId: sample.sampleId,
                                                },
                                            )
                                        }
                                    />
                                </TD>
                                <TD align="center">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled
                                        className="bg-[#0f141a] border-[#1e242a] text-[#6b7480] hover:bg-[#0f141a]"
                                    >
                                        Compute
                                    </Button>
                                </TD>
                            </TR>
                        ))}
                    </tbody>
                </DataTable>
            </DataCard>
        </BackofficePage>
    );
}

function ComputeMenu({
    missingLabel,
    recomputeLabel,
    onMissing,
    onRecompute,
}: {
    missingLabel: string;
    recomputeLabel: string;
    onMissing: () => void;
    onRecompute: () => void;
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className="min-w-[120px] bg-[#0f141a] border-[#1e242a] text-[#d8dde2] hover:bg-[#1a2126] hover:text-white"
                >
                    Compute
                    <ChevronDownIcon className="h-3 w-3 ml-1.5" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                className="w-56 bg-[#161c22] border-[#1e242a] text-[#d8dde2]"
                align="end"
            >
                <DropdownMenuItem
                    onClick={onMissing}
                    className="cursor-pointer focus:bg-[#1a2126]"
                >
                    {missingLabel}
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={onRecompute}
                    className="cursor-pointer focus:bg-[#1a2126]"
                >
                    {recomputeLabel}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
